// eslint-disable-next-line no-unused-vars
import React, { useMemo, forwardRef, useCallback } from 'react';
import styled from '@emotion/styled';
import { SkeletonText } from '@carbon/react';
import { bool, number, object, func, string, array } from 'prop-types';
import { ChevronUp, Calendar, Wallet, CreditCard, HandCoins, Gauge } from 'lucide-react';

import { CURRENCY_RON } from 'Constants';

const StyledCreditLeftSection = styled.div`
  position: relative;
  padding: 0 10px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  white-space: nowrap;

  .loading {
    white-space: nowrap;
    display: flex;
    justify-content: flex-end;
    // margin-right: 100px;
  }

  > div {
    text-align: right;

    p {
      font-size: 14px;

      span {
        margin-left: 5px;
        font-size: inherit;
        color: ${ props => props.theme.products.colors.isResealed };
      }
    }
      
  }

    @media screen and (max-width: ${ props => props.theme.device.tablet }) {
      display: none;
    }
`;

const StyledIndicator = styled.div`
  overflow: hidden;
  position: relative;
  height: 8px;
  border-radius: 5px;
  margin: 5px 10px;
  width: 65%;
  background-color: #c3c5c8;

  &:after {
    content: '';
    display: block;
    position: absolute;
    top: 0;
    left: 0;
    height: 8px;
    width: 100%;
    max-width: ${props => props.$percentage}%;
    transition: max-width 0.35s ease;
    background-color: ${props => props.theme.products.colors.isResealed};
    border-radius: 4px;
  }
`;

const DropdownMenu = styled.div`
  position: absolute;
  top: calc(100% + 14px);
  right: 0;
  width: 145%;
  color: ${props => props.theme.colors.darkBlue};
  background-color: ${props => props.theme.colors.white};
  border: 1px solid ${props => props.theme.products.colors.border};
  z-index: 9;
  box-shadow: 0 0 40px #00000029;
  opacity: ${props => props.$open ? 1 : 0};
  visibility: ${props => props.$open ? 'visible' : 'hidden'};
  transition: opacity 0.25s ease-out;

  &:after {
    display: block;
    position: absolute;
    top: -2px;
    right: 10px;
    width: 10px;
    height: 10px;
    background-color: #FFF;
    transform: rotate(45deg) translateX(-50%);
    content: '';
  }

  ul {
    padding: 0 12px;

    li {
      display: flex;
      align-items: center;
      font-size: 0.875rem;
      padding: 10px 0;
      color: ${props => props.theme.colors.darkGray};
      line-height: 1rem;
      border-bottom: 1px solid ${props => props.theme.colors.shadowGray};

      span {
        margin-left: 5px;
      }

      svg {
        width: 25px;
        margin-right: 5px;
      }
    }
  }
`;

const StyledSoldValueSection = styled.li`
  display: flex;
  align-items: center;
  justify-content: start;
  white-space: nowrap;

  .cds--skeleton__text {
    margin-left: 12px;
    min-width: 110px;
    max-width: 110px;
  }

  span {
    margin-left: 5px;
    font-size: inherit;
    color: ${ props => props.theme.colors.orange };
  }

  @media screen and (max-width: ${ props => props.theme.device.tablet }) {
    display: none;
  }
`;

const StyledChevronUp = styled(ChevronUp)`
  color: inherit;
  transform: scale(0.9) ${props => props.$menuOpen ? '' : 'rotate(180deg)'};
  margin-left: 8px;
  cursor: pointer;
`;

const CreditLeftSection = forwardRef(({
  menuOpen,
  credit,
  creditLoading,
  dueDays,
  creditRemaining,
  creditTotal,
  defaultCurrency,
  formatNumber,
  onToggle,
  theme,
  sold,
  soldLoading,
  isExternalReseller,
  t,
}, ref) => {
  const creditPercentage = useMemo(() => {
    if (!credit || !creditTotal || creditTotal <= 0) return 0;
    const percentage = (creditRemaining / creditTotal) * 100;
    return Math.max(0, Math.min(100, percentage));
  }, [credit, creditRemaining, creditTotal]);

  const consumedCredit = useMemo(() => (creditTotal || 0) - (creditRemaining || 0), [creditTotal, creditRemaining]);

  return (
    <StyledCreditLeftSection ref={ ref }>
      <div>
        {credit === undefined || creditLoading ? (
          <div className="loading">
            {t('user.creditLeft')}
            <SkeletonText />
          </div>
        ) : (
          <p>
            {t('user.creditLeft')}:
            <span>
              {formatNumber(creditRemaining, { currency: defaultCurrency })}
            </span>
          </p>
        )}
      </div>

      <StyledChevronUp
        onClick={ onToggle }
        $menuOpen={ menuOpen }
      />
      <DropdownMenu $open={ menuOpen }>
        <ul>
          <li>
            <Gauge /> {t('user.creditUsage')}:
            <StyledIndicator theme={ theme } $percentage={ creditPercentage } />
          </li>
          <StyledSoldValueSection>
            { (sold === undefined || soldLoading)
              ? <SkeletonText />
              : (
                <>
                  <HandCoins />
                  {t('user.soldValue')}:
                  {sold.map(element => {
                    // filter out RON for external resellers
                    if (isExternalReseller && element?.currency === CURRENCY_RON) {
                      return null;
                    }

                    return (
                      <span key={ element?.currency }>
                        {formatNumber(element?.value, {
                          currency: element?.currency,
                          maximumFractionDigits: element?.value === 0 ? 0 : 2
                        })}
                      </span>
                    );
                  })}
                </>
              )}
          </StyledSoldValueSection>
          <li>
            <Calendar />
            {dueDays === null || dueDays === undefined ? (
              <>
                {t('user.deadline')}:
                <SkeletonText />
              </>
            ) : (
              t('user.paymentTerm', { days: dueDays })
            )}
          </li>
          <li>
            <Wallet /> {t('user.consumedCredit')}:
            {!credit || creditLoading ? (
              <SkeletonText />
            ) : (
              <span>
                {formatNumber(consumedCredit, { currency: defaultCurrency })}
              </span>
            )}
          </li>
          <li>
            <CreditCard /> {t('user.creditLimit')}:
            {!credit || creditLoading ? (
              <SkeletonText />
            ) : (
              <span>{formatNumber(creditTotal, { currency: defaultCurrency })}</span>
            )}
          </li>
        </ul>
      </DropdownMenu>
    </StyledCreditLeftSection>
  );
});

CreditLeftSection.propTypes = {
  menuOpen: bool.isRequired,
  credit: object,
  creditLoading: bool.isRequired,
  dueDays: number,
  creditRemaining: number,
  creditTotal: number,
  defaultCurrency: string.isRequired,
  formatNumber: func.isRequired,
  onToggle: func.isRequired,
  theme: object.isRequired,
  sold: array,
  soldLoading: bool.isRequired,
  isExternalReseller: bool.isRequired,
  t: func.isRequired,
};

export default CreditLeftSection;
